import React, { useEffect, useMemo, useRef, useState } from "react"

import { AXIS_TEXT_FILL, DIVIDER_STROKE } from "@/components/charts/chartTheme"
import { formatNumber } from "@/utils/formatting/format-number"

import {
  computeKde,
  computeQuartileStats,
  type QuartileStats,
} from "./distributionStatsUtils"

interface ViolinGroup {
  id: string
  label: string
  values: number[]
  color: string
  /** Pre-computed quartile stats. When provided, ViolinPlot skips recomputing from values. */
  stats?: QuartileStats
}

interface ViolinPlotProps {
  groups: ViolinGroup[]
  units?: string
  height?: number
}

function niceStep(rawStep: number): number {
  if (rawStep <= 0) {
    return 1
  }
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude
  return (
    (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) *
    magnitude
  )
}

const PAD = { top: 24, right: 20, bottom: 60, left: 56 }
const MIN_BAND_PX = 80
const MAX_VIOLIN_PX = 60
const MAX_LABEL_CHARS = 18

export const ViolinPlot: React.FC<ViolinPlotProps> = ({
  groups,
  units = "",
  height = 280,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(560)

  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const plotH = height - PAD.top - PAD.bottom

  const naturalBandW =
    (containerWidth - PAD.left - PAD.right) / Math.max(groups.length, 1)
  const bandWidth = Math.max(MIN_BAND_PX, naturalBandW)
  const plotW = bandWidth * groups.length
  const svgW = PAD.left + plotW + PAD.right
  const needsScroll = svgW > containerWidth
  const violinW = Math.min(bandWidth * 0.5, MAX_VIOLIN_PX)

  const groupData = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        stats: g.stats ?? computeQuartileStats(g.values),
        kde: computeKde(g.values),
      })),
    [groups],
  )

  const { yMin, yMax } = useMemo(() => {
    let lo = Infinity
    let hi = -Infinity
    for (const g of groupData) {
      if (!g.stats) {
        continue
      }
      lo = Math.min(lo, g.stats.whiskerLow, ...g.stats.outliers)
      hi = Math.max(hi, g.stats.whiskerHigh, ...g.stats.outliers)
    }
    if (!isFinite(lo)) {
      return { yMin: 0, yMax: 1 }
    }
    const pad = (hi - lo) * 0.12 || 0.5
    return { yMin: lo - pad, yMax: hi + pad }
  }, [groupData])

  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

  const toY = (v: number) => PAD.top + (1 - (v - yMin) / (yMax - yMin)) * plotH

  const centerX = (i: number) => PAD.left + (i + 0.5) * bandWidth

  const yTicks = useMemo(() => {
    const range = yMax - yMin
    const step = niceStep(range / 5)
    const start = Math.ceil(yMin / step) * step
    const ticks: number[] = []
    for (
      let t = start;
      t <= yMax + 1e-9;
      t = Number((t + step).toPrecision(10))
    ) {
      ticks.push(Number(t.toPrecision(10)))
      if (ticks.length >= 8) {
        break
      }
    }
    return ticks
  }, [yMin, yMax])

  const fmt = (v: number) => `${formatNumber(v)}${units ? ` ${units}` : ""}`

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const i = Math.floor((mouseX - PAD.left) / bandWidth)
    if (i >= 0 && i < groups.length) {
      setHoveredIdx(i)
      setTooltipPos({ x: e.clientX, y: e.clientY })
    } else {
      setHoveredIdx(null)
    }
  }

  const hoveredGroup = hoveredIdx !== null ? groupData[hoveredIdx] : null
  const axisColor = DIVIDER_STROKE
  const tickColor = AXIS_TEXT_FILL

  return (
    <div ref={containerRef} className="w-full">
      <div
        tabIndex={0}
        role="region"
        aria-label="Scrollable chart"
        style={{
          overflowX: needsScroll ? "auto" : "visible",
          overflowY: "visible",
        }}
      >
        <svg
          role="img"
          aria-label="Violin plot distribution chart"
          width={svgW}
          height={height}
          onMouseMove={handleSvgMouseMove}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{ display: "block", overflow: "visible" }}
        >
          {/* Y axis spine */}
          <line
            x1={PAD.left}
            y1={PAD.top}
            x2={PAD.left}
            y2={PAD.top + plotH}
            stroke={axisColor}
            strokeWidth={1}
          />

          {/* Y grid lines + tick labels */}
          {yTicks.map((tick) => {
            const y = toY(tick)
            return (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  y1={y}
                  x2={PAD.left + plotW}
                  y2={y}
                  stroke={axisColor}
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 6}
                  y={y}
                  dominantBaseline="middle"
                  textAnchor="end"
                  fontSize={10}
                  fill={tickColor}
                >
                  {formatNumber(tick)}
                  {units ? ` ${units}` : ""}
                </text>
              </g>
            )
          })}

          {/* X axis spine */}
          <line
            x1={PAD.left}
            y1={PAD.top + plotH}
            x2={PAD.left + plotW}
            y2={PAD.top + plotH}
            stroke={axisColor}
            strokeWidth={1}
          />

          {/* One violin per group */}
          {groupData.map((g, i) => {
            if (!g.stats || g.kde.length === 0) {
              return null
            }
            const { q1, median, q3 } = g.stats
            const x = centerX(i)
            const isHovered = hoveredIdx === i

            // Find max density to scale width
            const maxDensity = Math.max(...g.kde.map((p) => p.density))
            if (maxDensity === 0) {
              return null
            }

            // Build mirrored violin path
            const halfW = violinW / 2
            const scaleDensity = (d: number) => (d / maxDensity) * halfW

            // Filter KDE points to visible y range (with small margin)
            const visible = g.kde.filter(
              (p) => p.value >= yMin && p.value <= yMax,
            )
            if (visible.length === 0) {
              return null
            }

            // Right side (top to bottom)
            const rightPath = visible
              .map((p) => `${x + scaleDensity(p.density)},${toY(p.value)}`)
              .join(" L")
            // Left side (bottom to top, mirrored)
            const leftPath = [...visible]
              .reverse()
              .map((p) => `${x - scaleDensity(p.density)},${toY(p.value)}`)
              .join(" L")

            const violinPath = `M${rightPath} L${leftPath} Z`

            // Inner quartile box width (narrower than violin)
            const innerW = 4

            return (
              <g key={g.id}>
                {/* Violin shape */}
                <path
                  d={violinPath}
                  fill={g.color}
                  fillOpacity={isHovered ? 0.35 : 0.2}
                  stroke={g.color}
                  strokeWidth={0.5}
                />
                {/* Inner IQR bar */}
                <rect
                  x={x - innerW / 2}
                  y={toY(q3)}
                  width={innerW}
                  height={Math.max(1, toY(q1) - toY(q3))}
                  fill={g.color}
                  fillOpacity={0.6}
                  rx={1}
                />
                {/* Median dot */}
                <circle
                  cx={x}
                  cy={toY(median)}
                  r={3.5}
                  fill="white"
                  stroke={g.color}
                  strokeWidth={1.5}
                />
                {/* X-axis label */}
                <text
                  x={x}
                  y={PAD.top + plotH + 14}
                  textAnchor="end"
                  fontSize={10}
                  fill={tickColor}
                  transform={`rotate(-35, ${x}, ${PAD.top + plotH + 14})`}
                >
                  {g.label.length > MAX_LABEL_CHARS
                    ? `${g.label.slice(0, MAX_LABEL_CHARS)}…`
                    : g.label}
                </text>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Tooltip */}
      {hoveredGroup?.stats && (
        <div
          className="fixed z-50 pointer-events-none bg-surface-default border border-coval-border rounded-md px-3 py-2 text-xs shadow-md"
          style={{ left: tooltipPos.x + 14, top: tooltipPos.y - 100 }}
        >
          <div className="font-semibold text-coval-text mb-1">
            {hoveredGroup.label}
          </div>
          <div className="space-y-0.5 text-coval-text-muted">
            <div>
              Max:{" "}
              <span className="text-coval-text font-medium">
                {fmt(hoveredGroup.stats.whiskerHigh)}
              </span>
            </div>
            <div>
              Q3:{" "}
              <span className="text-coval-text font-medium">
                {fmt(hoveredGroup.stats.q3)}
              </span>
            </div>
            <div>
              Median:{" "}
              <span className="text-coval-text font-medium">
                {fmt(hoveredGroup.stats.median)}
              </span>
            </div>
            <div>
              Q1:{" "}
              <span className="text-coval-text font-medium">
                {fmt(hoveredGroup.stats.q1)}
              </span>
            </div>
            <div>
              Min:{" "}
              <span className="text-coval-text font-medium">
                {fmt(hoveredGroup.stats.whiskerLow)}
              </span>
            </div>
            {hoveredGroup.stats.outliers.length > 0 && (
              <div className="text-coval-text-muted mt-0.5">
                {hoveredGroup.stats.outliers.length} outlier
                {hoveredGroup.stats.outliers.length > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
